import { useState } from 'react';
import type { Customer, PropertySearch } from '@metron/shared';
import { propertySearchSchema } from '@metron/shared';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { CustomerPicker } from '../CustomerPicker.tsx';
import { type FilterChipItem, FilterChipList } from '../search/FilterChipList.tsx';

/** Spec fields a user can add or edit here; paging and sort belong to the table. */
type PropertyFilterField = 'customerId' | 'text' | 'meterCountMin' | 'meterCountMax';

export interface PropertyFilterChip extends FilterChipItem<PropertyFilterField> {
  /** Fields cleared when the chip is removed (the meter range shares one chip). */
  fields: PropertyFilterField[];
}

/** One chip per active filter, in a fixed order. The customer shows by name, never by id. */
export function describePropertyFilters(
  spec: PropertySearch,
  customerName?: string,
): PropertyFilterChip[] {
  const chips: PropertyFilterChip[] = [];
  if (spec.customerId !== undefined) {
    chips.push({
      id: 'customerId',
      label: `Customer: ${customerName ?? 'selected customer'}`,
      fields: ['customerId'],
      editable: true,
    });
  }
  if (spec.text !== undefined) {
    chips.push({ id: 'text', label: `Matches “${spec.text}”`, fields: ['text'], editable: true });
  }
  const { meterCountMin: min, meterCountMax: max } = spec;
  const range = min !== undefined && max !== undefined
    ? `${min}–${max} meters`
    : min !== undefined
    ? `≥ ${min} meters`
    : max !== undefined
    ? `≤ ${max} meters`
    : null;
  if (range) {
    chips.push({
      id: 'meterCountMin',
      label: range,
      fields: ['meterCountMin', 'meterCountMax'],
      editable: true,
    });
  }
  return chips;
}

/** Drop the given filter fields and return to page 1. */
export function withoutPropertyFields(
  spec: PropertySearch,
  fields: PropertyFilterField[],
): PropertySearch {
  const next: PropertySearch = { ...spec, page: 1 };
  for (const f of fields) delete next[f];
  return next;
}

interface EditorProps {
  spec: PropertySearch;
  customers: Customer[];
  customersLoading: boolean;
  focusField: PropertyFilterField | null;
  onApply: (next: PropertySearch) => void;
}

function PropertyFilterEditor(
  { spec, customers, customersLoading, focusField, onApply }: EditorProps,
) {
  const [customerId, setCustomerId] = useState<number | undefined>(spec.customerId);
  const [text, setText] = useState(spec.text ?? '');
  const [min, setMin] = useState(
    spec.meterCountMin === undefined ? '' : String(spec.meterCountMin),
  );
  const [max, setMax] = useState(
    spec.meterCountMax === undefined ? '' : String(spec.meterCountMax),
  );
  const [errors, setErrors] = useState<string[]>([]);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const num = (raw: string) => (raw.trim() === '' ? undefined : Number(raw));
    const candidate: Record<string, unknown> = {
      ...spec,
      page: 1,
      customerId,
      text: text.trim() === '' ? undefined : text,
      meterCountMin: num(min),
      meterCountMax: num(max),
    };
    const parsed = propertySearchSchema.safeParse(
      Object.fromEntries(Object.entries(candidate).filter(([, v]) => v !== undefined)),
    );
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onApply(parsed.data);
  };

  return (
    <form onSubmit={apply} className='space-y-3' aria-label='Edit filters'>
      <div className='space-y-1'>
        <Label htmlFor='property-search-customer'>Customer</Label>
        <CustomerPicker
          inputId='property-search-customer'
          customers={customers}
          isLoading={customersLoading}
          selectedCustomerId={customerId}
          onSelect={setCustomerId}
          onClear={() => setCustomerId(undefined)}
        />
      </div>
      <div className='space-y-1'>
        <Label htmlFor='property-search-text'>Property name or Metron ID</Label>
        <Input
          id='property-search-text'
          value={text}
          autoFocus={focusField === 'text'}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1'>
          <Label htmlFor='property-search-meterCountMin'>Meters at least</Label>
          <Input
            id='property-search-meterCountMin'
            type='number'
            inputMode='numeric'
            min={0}
            step={1}
            value={min}
            autoFocus={focusField === 'meterCountMin'}
            onChange={(e) => setMin(e.target.value)}
          />
        </div>
        <div className='space-y-1'>
          <Label htmlFor='property-search-meterCountMax'>Meters at most</Label>
          <Input
            id='property-search-meterCountMax'
            type='number'
            inputMode='numeric'
            min={0}
            step={1}
            value={max}
            onChange={(e) => setMax(e.target.value)}
          />
        </div>
      </div>
      {errors.length > 0 && (
        <ul role='alert' className='text-sm text-destructive list-disc pl-5'>
          {/* Replaced wholesale on each submit, and two issues can share a message. */}
          {errors.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      <div className='flex justify-end'>
        <Button type='submit' size='sm'>Apply</Button>
      </div>
    </form>
  );
}

interface PropertySearchFiltersProps {
  spec: PropertySearch;
  customers: Customer[];
  customersLoading: boolean;
  onChange: (next: PropertySearch) => void;
}

/** Active property filters as chips plus an Add filter editor. */
export function PropertySearchFilters(
  { spec, customers, customersLoading, onChange }: PropertySearchFiltersProps,
) {
  const customerName = customers.find((c) => c.id === spec.customerId)?.waterscopeAccountName;
  return (
    <FilterChipList
      chips={describePropertyFilters(spec, customerName)}
      emptyText='No filters: showing all properties.'
      onRemove={(chip) => onChange(withoutPropertyFields(spec, chip.fields))}
      renderEditor={(focusField, close) => (
        <PropertyFilterEditor
          spec={spec}
          customers={customers}
          customersLoading={customersLoading}
          focusField={focusField}
          onApply={(next) => {
            close();
            onChange(next);
          }}
        />
      )}
    />
  );
}
